/**
 * Cloudflare Turnstile, shown only when the server asks for it (before a scout registers to earn).
 * The widget sits in a small panel over the canvas and resolves with its token, or rejects.
 */
type Turnstile = { render(el: HTMLElement, o: Record<string, unknown>): string; remove(id: string): void };

let loading: Promise<Turnstile> | null = null;

function load(): Promise<Turnstile> {
  const w = window as Window & { turnstile?: Turnstile };
  if (w.turnstile) return Promise.resolve(w.turnstile);
  loading ??= new Promise((ok, fail) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () => (w.turnstile ? ok(w.turnstile) : fail(new Error("The human check did not load.")));
    s.onerror = () => {
      loading = null;
      fail(new Error("The human check did not load."));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export async function humanCheck(siteKey: string): Promise<string> {
  if (typeof document === "undefined") throw new Error("The human check needs the browser page.");
  const ts = await load();
  const host = document.getElementById("bezel") ?? document.body;
  const box = document.createElement("div");
  box.id = "captcha-box";
  box.style.cssText = "position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:20;padding:10px;background:#07080f;border:2px solid #ffc84a;border-radius:10px";
  host.appendChild(box);
  return new Promise((ok, fail) => {
    let id = "";
    const done = (fn: () => void) => {
      try {
        ts.remove(id);
      } catch {
        /* already gone */
      }
      box.remove();
      fn();
    };
    id = ts.render(box, {
      sitekey: siteKey,
      theme: "dark",
      callback: (token: string) => done(() => ok(token)),
      "error-callback": () => done(() => fail(new Error("The human check failed. Try again."))),
      "timeout-callback": () => done(() => fail(new Error("The human check timed out. Try again."))),
    });
  });
}
